/**
 * auth.ts — Authentication screens (gate key, login, signup, role select)
 */

import { api } from './api.js';
import { navigateToHome } from './profile.js';
import { navigate } from './router.js';
import { state } from './state.js';
import type { AuthResponse } from './types/api.js';
import { PRIVACY_URL, TERMS_URL } from '../../shared/legal.js';
import type { UserType } from '../../shared/types.js';
import type { UserRole } from './types/domain.js';
import { escapeHtml, showToast } from './ui.js';

const GATE_KEY = 'swipehome2026';
let _selectedRole: UserRole | null = null;
// Investor pivot — 'investor' is the primary signup path but maps to
// role='buyer' in the backend (investors still need BuyerPreferences,
// can swipe and like). The user_type flag routes the UX.
let _selectedUserType: UserType = 'buyer';

void escapeHtml; // Kept in case future auth screens render user-supplied strings.

export function renderGateKey(): HTMLElement {
  if (sessionStorage.getItem('sh_gate_unlocked') === '1') {
    setTimeout(() => navigate('login'), 0);
    return document.createElement('div');
  }

  const div = document.createElement('div');
  div.className = 'auth-screen fade-in';
  div.innerHTML = `
    <div class="auth-logo">
      <div class="auth-logo-icon">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <path d="M20 4L4 18H10V32H16V24H24V32H30V18H36L20 4Z" fill="#6366f1"/>
          <path d="M20 4L4 18H10V32H16V24H24V32H30V18H36L20 4Z" fill="url(#grad)" opacity="0.3"/>
          <defs><linearGradient id="grad" x1="4" y1="4" x2="36" y2="32"><stop stop-color="#818cf8"/><stop offset="1" stop-color="#6366f1"/></linearGradient></defs>
        </svg>
      </div>
      <h1>swipe<span>home</span></h1>
      <p style="color:var(--text-muted);font-size:13px">Private Beta — Authorized Access Only</p>
    </div>
    <div class="auth-form">
      <div id="gate-error" class="auth-error hidden"></div>
      <div class="input-group">
        <label>Access Key</label>
        <div class="input-password-wrap">
          <input type="password" class="input" id="gate-key-input" placeholder="Enter private access key" autocomplete="off">
          <button class="input-password-toggle" onclick="togglePasswordVisibility('gate-key-input', this)" type="button">
            <i data-feather="eye-off" width="18"></i>
          </button>
        </div>
      </div>
      <button class="btn btn-primary btn-block btn-lg mt-16" onclick="handleGateKey()">
        <i data-feather="unlock" width="18"></i> Enter
      </button>
    </div>
  `;

  setTimeout(() => {
    const input = div.querySelector<HTMLInputElement>('#gate-key-input');
    if (input) {
      input.focus();
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') handleGateKey();
      });
    }
  }, 100);

  return div;
}

export function handleGateKey(): void {
  const input = document.getElementById('gate-key-input') as HTMLInputElement | null;
  const errorEl = document.getElementById('gate-error');
  const key = input?.value?.trim();

  if (!key) {
    if (errorEl) {
      errorEl.textContent = 'Please enter an access key';
      errorEl.classList.remove('hidden');
    }
    return;
  }

  if (key !== GATE_KEY) {
    if (errorEl) {
      errorEl.textContent = 'Invalid access key';
      errorEl.classList.remove('hidden');
    }
    if (input) {
      input.value = '';
      input.focus();
    }
    return;
  }

  sessionStorage.setItem('sh_gate_unlocked', '1');
  navigate('login');
}

export function renderLogin(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'auth-screen fade-in';
  div.innerHTML = `
    <div class="auth-logo">
      <div class="auth-logo-icon">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <path d="M20 4L4 18H10V32H16V24H24V32H30V18H36L20 4Z" fill="#6366f1"/>
          <path d="M20 4L4 18H10V32H16V24H24V32H30V18H36L20 4Z" fill="url(#grad)" opacity="0.3"/>
          <defs><linearGradient id="grad" x1="4" y1="4" x2="36" y2="32"><stop stop-color="#818cf8"/><stop offset="1" stop-color="#6366f1"/></linearGradient></defs>
        </svg>
      </div>
      <h1>swipe<span>home</span></h1>
      <p>Deals that pencil.</p>
    </div>
    <div class="auth-form">
      <div id="auth-error" class="auth-error hidden"></div>
      <div class="input-group">
        <label>Email</label>
        <input type="email" class="input" id="login-email" placeholder="you@example.com" autocomplete="email">
      </div>
      <div class="input-group">
        <label>Password</label>
        <div class="input-password-wrap">
          <input type="password" class="input" id="login-password" placeholder="Your password" autocomplete="current-password">
          <button class="input-password-toggle" onclick="togglePasswordVisibility('login-password', this)" type="button">
            <i data-feather="eye-off" width="18"></i>
          </button>
        </div>
      </div>
      <button class="btn btn-primary btn-block btn-lg mt-16" id="login-btn" onclick="handleLogin()">Log In</button>
    </div>
    <div class="auth-footer">
      <span class="auth-footer-text">Don't have an account?</span>
      <a class="auth-footer-link" onclick="navigate('signup')">Create Account</a>
    </div>
    <div class="auth-legal-footer">
      <a href="${PRIVACY_URL}" target="_blank" rel="noopener">Privacy Policy</a>
      <span>&middot;</span>
      <a href="${TERMS_URL}" target="_blank" rel="noopener">Terms of Service</a>
    </div>
  `;

  setTimeout(() => {
    const emailInput = div.querySelector<HTMLInputElement>('#login-email');
    if (emailInput) emailInput.focus();
    div.querySelectorAll<HTMLInputElement>('.input').forEach((input) => {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') handleLogin();
      });
    });
  }, 100);

  return div;
}

export async function handleLogin(): Promise<void> {
  const emailInput = document.getElementById('login-email') as HTMLInputElement | null;
  const passwordInput = document.getElementById('login-password') as HTMLInputElement | null;
  const email = emailInput?.value?.trim();
  const password = passwordInput?.value;
  const errorEl = document.getElementById('auth-error');
  const btn = document.getElementById('login-btn') as HTMLButtonElement | null;

  if (!email || !password) {
    if (errorEl) {
      errorEl.textContent = 'Please enter email and password';
      errorEl.classList.remove('hidden');
    }
    return;
  }

  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Signing in...';
  }
  errorEl?.classList.add('hidden');

  try {
    const data = await api<AuthResponse>('POST', '/auth/login', { email, password });
    state.token = data.token;
    state.user = data.user;
    state.role = 'buyer';
    localStorage.setItem('swipehome_token', state.token);
    localStorage.setItem('swipehome_user', JSON.stringify(state.user));
    const displayName = data.user.full_name || 'there';
    showToast(`Welcome back, ${displayName}!`, 'success');
    navigateToHome();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Login failed';
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    }
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Sign In';
    }
  }
}

/**
 * Consent sentence under the primary button on every signup screen —
 * the form (Continue) and the role step (Create Account). The checkbox
 * on the form is the affirmative agreement; this line stays visible on
 * the step that actually creates the account.
 */
function renderSignupConsent(): string {
  return `
    <p class="auth-consent">
      By creating an account, you agree to the
      <a href="${TERMS_URL}" target="_blank" rel="noopener">Terms of Service</a> and
      <a href="${PRIVACY_URL}" target="_blank" rel="noopener">Privacy Policy</a>.
    </p>
  `;
}

export function renderSignup(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'auth-screen fade-in';
  div.innerHTML = `
    <div class="auth-logo">
      <h1>Join Swipe<span>Home</span></h1>
      <p>Create your account to get started</p>
    </div>
    <div class="auth-form">
      <div id="signup-error" class="auth-error hidden"></div>
      <div class="input-row">
        <div class="input-group">
          <label>First Name</label>
          <input type="text" class="input" id="signup-first" placeholder="Jordan">
        </div>
        <div class="input-group">
          <label>Last Name</label>
          <input type="text" class="input" id="signup-last" placeholder="Smith">
        </div>
      </div>
      <div class="input-group">
        <label>Email</label>
        <input type="email" class="input" id="signup-email" placeholder="you@example.com" autocomplete="email">
      </div>
      <div class="input-group">
        <label>Phone</label>
        <input type="tel" class="input" id="signup-phone" placeholder="(555) 123-4567">
      </div>
      <div class="input-group">
        <label>Password</label>
        <input type="password" class="input" id="signup-password" placeholder="Min 6 characters" autocomplete="new-password">
      </div>
      <label class="auth-tos-check">
        <input type="checkbox" id="signup-tos">
        <span>I agree to the <a href="${TERMS_URL}" target="_blank" rel="noopener">Terms of Service</a> and <a href="${PRIVACY_URL}" target="_blank" rel="noopener">Privacy Policy</a></span>
      </label>
      <button class="btn btn-primary btn-block btn-lg mt-16" id="signup-btn" onclick="handleSignupStep1()">Continue</button>
      ${renderSignupConsent()}
    </div>
    <div class="auth-footer">
      Already have an account? <a onclick="navigate('login')">Sign in</a>
    </div>
    <div class="auth-legal-footer">
      <a href="${PRIVACY_URL}" target="_blank" rel="noopener">Privacy Policy</a>
      <span>&middot;</span>
      <a href="${TERMS_URL}" target="_blank" rel="noopener">Terms of Service</a>
    </div>
  `;
  return div;
}

interface SignupData {
  full_name: string;
  email: string;
  phone: string | null;
  password: string;
}

export function handleSignupStep1(): void {
  const getVal = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value?.trim();
  const first = getVal('signup-first');
  const last = getVal('signup-last');
  const email = getVal('signup-email');
  const phone = getVal('signup-phone');
  const password = (document.getElementById('signup-password') as HTMLInputElement | null)?.value;
  const errorEl = document.getElementById('signup-error');

  if (!first || !last || !email || !password) {
    if (errorEl) {
      errorEl.textContent = 'Please fill in all required fields';
      errorEl.classList.remove('hidden');
    }
    return;
  }
  if (password.length < 6) {
    if (errorEl) {
      errorEl.textContent = 'Password must be at least 6 characters';
      errorEl.classList.remove('hidden');
    }
    return;
  }
  const tosCheck = document.getElementById('signup-tos') as HTMLInputElement | null;
  if (!tosCheck?.checked) {
    if (errorEl) {
      errorEl.textContent = 'Please agree to the Terms of Service and Privacy Policy to continue';
      errorEl.classList.remove('hidden');
    }
    return;
  }

  const signupData: SignupData = {
    full_name: `${first} ${last}`,
    email,
    phone: phone || null,
    password,
  };
  // Stored on state as a scratch field during the signup flow.
  (state as unknown as { _signupData: SignupData | null })._signupData = signupData;
  navigate('roleSelect');
}

export function renderRoleSelect(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'auth-screen fade-in';
  // Investor-first convergence — the consumer hero is the only consumer
  // option for now. The legacy "Buyer" secondary card was removed; the
  // future home-shopper layer can be reintroduced once the platform has
  // more users. Agent + Listing remain as the alternative role paths.
  div.innerHTML = `
    <div class="auth-logo" style="margin-bottom:24px">
      <h1 style="font-size:24px">I am a...</h1>
      <p>Deals that find you. Every cost revealed.</p>
    </div>
    <div id="role-error" class="auth-error hidden"></div>
    <div class="role-card role-card-hero" onclick="selectRole(this, 'buyer', 'investor')">
      <div class="role-icon role-icon-hero"><i data-feather="trending-up" width="26" height="26"></i></div>
      <div class="role-card-body">
        <h3>Real estate investor</h3>
        <p>Cash flow, cap rate, DSCR — every deal analyzed on the feed</p>
      </div>
      <i data-feather="chevron-right" width="18" class="role-card-chev"></i>
    </div>
    <button class="btn btn-primary btn-block btn-lg" id="role-submit-btn" onclick="handleSignup()" disabled>Create Account</button>
    ${renderSignupConsent()}
  `;
  return div;
}

export function selectRole(el: HTMLElement, _role: UserRole, userType?: UserType): void {
  // Investor-only signup — every account is a real-estate investor.
  _selectedRole = 'buyer';
  _selectedUserType = userType || 'investor';
  document.querySelectorAll('.role-card').forEach((c) => c.classList.remove('selected'));
  el.classList.add('selected');
  const btn = document.getElementById('role-submit-btn') as HTMLButtonElement | null;
  if (btn) btn.disabled = false;
}

interface SignupPayload extends SignupData {
  role: 'buyer';
  user_type?: UserType;
}

export async function handleSignup(): Promise<void> {
  const btn = document.getElementById('role-submit-btn') as HTMLButtonElement | null;
  const errorEl = document.getElementById('role-error');

  if (!_selectedRole) return;

  const stash = state as unknown as {
    _signupData: SignupData | null;
    _licenseNumber?: string;
    _licenseState?: string;
  };
  const signupData = stash._signupData;
  if (!signupData) return;

  // Investor-only: every signup is role='buyer' / user_type='investor'.
  const payload: SignupPayload = {
    ...signupData,
    role: 'buyer',
    user_type: _selectedUserType,
  };

  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Creating account...';
  }
  errorEl?.classList.add('hidden');

  try {
    const data = await api<AuthResponse>('POST', '/auth/signup', payload);
    state.token = data.token;
    state.user = data.user;
    state.role = _selectedRole;
    localStorage.setItem('swipehome_token', state.token);
    localStorage.setItem('swipehome_user', JSON.stringify(state.user));
    stash._signupData = null;
    showToast('Account created!', 'success');

    // Every signup goes through investor onboarding (strategy → markets →
    // criteria → portfolio size) before landing on the feed.
    navigate('investorOnboarding');
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Signup failed';
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    }
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Create Account';
    }
  }
}
